import { ApiError } from './api-error'
import { Storage } from './storage'
import type {
    Api,
    EventApi,
    EventPaymentGatewayValidationResult,
    EventSubmitError,
    EventSubmitResult,
    GiftCardApi,
    GiftCardSubmitError,
    GiftCardSubmitResult,
    SerialTicketApi,
    SerialTicketSubmitError,
    SerialTicketSubmitResult,
    SessionApi,
} from './types'

// If URL contains username and/or password, moves it to headers and returns clean URL
const moveAuth = (urlString: string, headers: Record<string, string>) => {
    const url = new URL(urlString)

    if (url.username || url.password) {
        headers.authorization = 'Basic ' + window.btoa(url.username + ':' + url.password)
        url.username = ''
        url.password = ''
    }

    return url.toString()
}

class FetchError extends Error {
    public readonly statusCode: number
    public readonly body: unknown

    constructor(statusCode: number, body: unknown) {
        super('FetchError')
        Object.setPrototypeOf(this, new.target.prototype)
        this.statusCode = statusCode
        this.body = body
    }
}

const checkResponse = async (response: Response) => {
    if (response.status >= 200 && response.status < 300) {
        const sessionId = response.headers.get('kinola-session-id')

        if (sessionId) {
            Storage.update({ sessionId })
        }
    } else {
        let error: Error | undefined

        try {
            const resBody = (await response.json()) as Record<string, unknown>
            const { code, message } = resBody

            if (typeof code === 'string' && typeof message === 'string') {
                error = new ApiError(code, message)
            } else {
                error = new FetchError(response.status, resBody)
            }
        } catch (err) {
            // Use fallback error
        }

        throw error || new Error(`API call failed, status ${response.status}`)
    }
}

const getHeaderWithSessionId = (): Record<string, string> => ({
    'kinola-session-id': Storage.get('sessionId'),
})

const apiWithoutBody = async <T>(method: 'GET' | 'DELETE', url: string): Promise<T> => {
    const headers = getHeaderWithSessionId()
    const cleanUrl = moveAuth(url, headers)
    const response = await fetch(cleanUrl, { method, headers })
    await checkResponse(response)
    return response.json()
}

const apiWithBody = async <T>(
    method: 'POST' | 'PATCH' | 'PUT',
    url: string,
    body?: unknown,
): Promise<T> => {
    const headers = getHeaderWithSessionId()
    const init: RequestInit = { method, headers }

    if (body !== undefined) {
        headers['content-type'] = 'application/json'
        init.body = JSON.stringify(body)
    }

    const cleanUrl = moveAuth(url, headers)
    const response = await fetch(cleanUrl, init)
    await checkResponse(response)
    return response.json()
}

const apiGet = async <T>(url: string): Promise<T> => apiWithoutBody('GET', url)
const apiDelete = async <T>(url: string): Promise<T> => apiWithoutBody('DELETE', url)
const apiPost = async <T>(url: string, body?: unknown): Promise<T> => apiWithBody('POST', url, body)
const apiPatch = async <T>(url: string, body?: unknown): Promise<T> =>
    apiWithBody('PATCH', url, body)
const apiPut = async <T>(url: string, body?: unknown): Promise<T> => apiWithBody('PUT', url, body)

export const createFetchEventApi = (baseUrl: string): EventApi => {
    const getEventUrl = (eventId: string) => `${baseUrl}/events/${eventId}`

    return {
        getEvent: async (eventId, lang) => {
            return apiGet(`${getEventUrl(eventId)}/${lang}`)
        },

        addSeat: async (eventId, lang, rowNumber, seatNumber) => {
            return apiPost(`${getEventUrl(eventId)}/seat/${rowNumber}/${seatNumber}`, { lang })
        },

        setProductVariantQuantity: async (eventId, lang, variantId, quantity) => {
            return apiPost(`${getEventUrl(eventId)}/product-variants`, {
                lang,
                variantId,
                quantity,
            })
        },

        removeSeat: async (eventId, lang, rowNumber, seatNumber) => {
            return apiDelete(`${getEventUrl(eventId)}/seat/${rowNumber}/${seatNumber}/${lang}`)
        },

        updateSeat: async (eventId, lang, rowNumber, seatNumber, ticketTypeId) => {
            return apiPatch(`${getEventUrl(eventId)}/seat/${rowNumber}/${seatNumber}`, {
                lang,
                ticketTypeId,
            })
        },

        setTicketAmount: async (eventId, lang, ticketTypeId, amount) => {
            return apiPost(`${getEventUrl(eventId)}/ticket`, { lang, ticketTypeId, amount })
        },

        applyDiscount: async (eventId, lang, code) => {
            return apiPost(`${getEventUrl(eventId)}/card/${code}`, { lang })
        },

        removeCard: async (eventId, lang, code) => {
            return apiDelete(`${getEventUrl(eventId)}/card/${code}/${lang}`)
        },

        removeSerialTicket: async (eventId, lang, serialTicketId) => {
            return apiDelete(
                `${getEventUrl(eventId)}/discounts/serial-ticket/${serialTicketId}/${lang}`,
            )
        },

        validateSelectedPaymentGateway: async (eventId, customer, selectedPaymentGateway) => {
            try {
                return await apiPost<EventPaymentGatewayValidationResult>(
                    `${getEventUrl(eventId)}/validatePaymentGateway`,
                    { customer, selectedPaymentGateway },
                )
            } catch (error) {
                if (error instanceof FetchError && error.statusCode === 422) {
                    return error.body as EventSubmitError
                }

                throw error
            }
        },

        submit: async (eventId, customer, selectedPaymentGateway, returnUrl) => {
            try {
                return await apiPost<EventSubmitResult>(`${getEventUrl(eventId)}/submit`, {
                    customer,
                    selectedPaymentGateway,
                    returnUrl,
                })
            } catch (error) {
                if (error instanceof ApiError && error.code === 'no-tickets') {
                    // TODO avoid full refresh, just reload data?
                    window.location.reload()
                }

                if (error instanceof FetchError && error.statusCode === 422) {
                    return error.body as EventSubmitError
                }

                throw error
            }
        },

        reset: async (eventId) => {
            return apiPost(`${getEventUrl(eventId)}/reset`)
        },

        getEvents: async () => {
            return apiGet(`${baseUrl}/events`)
        },
    }
}

export const createFetchGiftCardApi = (baseUrl: string): GiftCardApi => {
    const giftCardUrl = `${baseUrl}/gift-cards`

    return {
        getData: async () => {
            return apiGet(`${giftCardUrl}/data`)
        },
        submit: async (amount, email, returnUrl, selectedPaymentGateway) => {
            try {
                return await apiPost<GiftCardSubmitResult>(`${giftCardUrl}/submit`, {
                    amount,
                    email,
                    returnUrl,
                    selectedPaymentGateway,
                })
            } catch (error) {
                if (error instanceof FetchError && error.statusCode === 422) {
                    return error.body as GiftCardSubmitError
                }

                throw error
            }
        },
        reset: async () => {
            await apiPost(`${giftCardUrl}/reset`)
        },
    }
}

export const createFetchSerialTicketApi = (baseUrl: string): SerialTicketApi => {
    const serialTicketUrl = `${baseUrl}/serial-tickets`

    return {
        getData: async () => {
            return apiGet(`${serialTicketUrl}/data`)
        },
        submit: async (typeId, email, returnUrl, selectedPaymentGateway) => {
            try {
                return await apiPost<SerialTicketSubmitResult>(`${serialTicketUrl}/submit`, {
                    typeId,
                    email,
                    returnUrl,
                    selectedPaymentGateway,
                })
            } catch (error) {
                if (error instanceof FetchError && error.statusCode === 422) {
                    return error.body as SerialTicketSubmitError
                }

                throw error
            }
        },
        reset: async () => {
            await apiPost(`${serialTicketUrl}/reset`)
        },
    }
}

export const createFetchSessionApi = (baseUrl: string): SessionApi => {
    return {
        getSession: async () => {
            return apiGet(`${baseUrl}/session`)
        },
        updateSession: async (session) => {
            return apiPut(`${baseUrl}/session`, session)
        },
    }
}

export const createFetchApi = (baseUrl: string): Api => {
    return {
        events: createFetchEventApi(baseUrl),
        giftCards: createFetchGiftCardApi(baseUrl),
        serialTickets: createFetchSerialTicketApi(baseUrl),
        session: createFetchSessionApi(baseUrl),

        getGeneralData: async () => apiGet(`${baseUrl}/general-data`),
    }
}
